import { ALL_FIAS_FIELDS, FiasFields, FiasFullValue, FiasValue, GarHouse } from "../FiasWidgetTypes";
import { FiasAddress } from "../../../Utils/FiasAddressUtils";
import { divisionType, FiasExtendFieldsType, ParsedExtendedFiasField } from "../../../FiasInput/Types/FiasTypes";
import { buildFullRegionCode, findRegionCodeByName } from "../../../FiasInput/Utils/Convertations/Region/Region";
import { IterUtils } from "../../../../../../../../Common/IterableUtils";
import { reject } from "../../../../../../../../Common/TypingUtils";
import { RegionsType } from "../../../FiasInput/FiasApi";
import { setIn } from "../../../../../../Helpers/ReduxStateHelpers";

import { convertDivisionType } from "./DivisionTypeConverter";

export class FiasValueBuilder {
    public static buildValue(
        fiasAddress: FiasAddress,
        extendedFields: ParsedExtendedFiasField[],
        regionsList?: RegionsType
    ): FiasValue {
        const fiasValue: FiasValue = {
            ...FiasValueBuilder.buildDefaultFiasRootValue(
                extendedFields.filter(it => it.type !== FiasExtendFieldsType.guid),
                fiasAddress
            ),
            address: {},
        };

        const indexExtendedField = extendedFields.find(it => it.type === FiasExtendFieldsType.index);
        if (fiasAddress[indexExtendedField?.path ?? ""]) {
            fiasValue.postalCode = fiasAddress[indexExtendedField?.path ?? ""];
        }

        const filteredExtendedFields = extendedFields.filter(
            it => ![FiasExtendFieldsType.guid, FiasExtendFieldsType.index].includes(it.type)
        );
        const groupedExtendedFields: Map<FiasFields, ParsedExtendedFiasField[]> = IterUtils.groupBy(
            filteredExtendedFields,
            it =>
                (it.sourceValue.at(0)?.fiasApiPath?.split(".").at(0) as FiasFields) ??
                reject(`FIAS API path not found: ${it.path}`)
        );

        for (const [fiasField, groupExtendedFields] of groupedExtendedFields) {
            fiasValue.address = {
                ...fiasValue.address,
                ...FiasValueBuilder.buildDefaultFiasAddressValue(fiasField, groupExtendedFields, fiasAddress),
                ...FiasValueBuilder.buildRegionFiasValue(fiasField, groupExtendedFields, fiasAddress, regionsList),
                ...FiasValueBuilder.buildHouseFiasValue(fiasField, groupExtendedFields, fiasAddress),
            };
        }

        return fiasValue;
    }

    public static buildFullValue(
        fiasAddress: FiasAddress,
        extendedFields: ParsedExtendedFiasField[],
        regionsList?: RegionsType
    ): FiasFullValue {
        const fiasValue = FiasValueBuilder.buildValue(fiasAddress, extendedFields, regionsList);
        const fiasFullValue: FiasFullValue = {
            ...fiasValue,
            address: fiasValue.address ?? {},
            addressErrors: {},
            division: FiasValueBuilder.buildDivisionValue(extendedFields, fiasAddress),
            postalCode: "",
            addressString: "",
            fiasId: "",
        };

        return fiasFullValue;
    }

    private static buildDefaultFiasRootValue(
        extendedFields: ParsedExtendedFiasField[],
        fiasAddress: FiasAddress
    ): Partial<FiasFullValue> {
        let fiasFullValue: Partial<FiasFullValue> = {};

        for (const extendedField of extendedFields) {
            const fiasApiPath = extendedField.sourceValue.at(0)?.fiasApiPath ?? "";
            if (!ALL_FIAS_FIELDS.some(it => fiasApiPath.startsWith(String(it)))) {
                const value: string | undefined = fiasAddress[extendedField?.path ?? ""];
                if (!value) {
                    continue;
                }

                fiasFullValue = setIn(fiasFullValue, fiasApiPath.split("."), value);
            }
        }

        return fiasFullValue;
    }

    private static buildDefaultFiasAddressValue(
        fiasField: FiasFields,
        extendedFields: ParsedExtendedFiasField[],
        fiasAddress: FiasAddress
    ): FiasFullValue["address"] {
        const fiasFieldName = String(fiasField);
        const extendedField = extendedFields.find(it => {
            const fiasApiPath = it.sourceValue.at(0)?.fiasApiPath ?? "";
            const fiasApiFieldName = fiasApiPath.split(".").at(-1)?.toLowerCase() ?? "";
            // Этот хардкод тут потому что надо достать самое релевантное значение из иннера для модели виджета
            return fiasApiFieldName.includes("name") || fiasApiFieldName.includes("number");
        });
        const value: string | undefined = fiasAddress[extendedField?.path ?? ""];

        if (!value) {
            return {};
        }

        return {
            [fiasFieldName]: {
                name: value,
                text: value,
            },
        };
    }

    private static buildRegionFiasValue(
        fiasField: FiasFields,
        extendedFields: ParsedExtendedFiasField[],
        fiasAddress: FiasAddress,
        regionsList?: RegionsType
    ): FiasFullValue["address"] {
        if (fiasField === FiasFields.region) {
            const regionCodeExtendedField = extendedFields.find(it =>
                it.sourceValue.at(0)?.fiasApiPath?.endsWith("code")
            );
            const regionNameExtendedField = extendedFields.find(it => {
                const fiasApiPath = it.sourceValue.at(0)?.fiasApiPath ?? "";
                return fiasApiPath.endsWith("official") || fiasApiPath.endsWith("name");
            });

            const fiasCodeValue = fiasAddress[regionCodeExtendedField?.path ?? ""];
            let regionCode = "";
            // check if code is really code
            if (!isNaN(Number(fiasCodeValue))) {
                regionCode = buildFullRegionCode(fiasCodeValue ?? "");
            } else {
                regionCode = findRegionCodeByName(fiasCodeValue ?? "", regionsList) ?? fiasCodeValue ?? "";
            }

            const regionName =
                fiasAddress[regionNameExtendedField?.path ?? ""] ||
                regionsList?.find(({ region }) => region.code === regionCode)?.region.official;

            if (!regionCode && !regionName) {
                return {};
            }

            return {
                [String(FiasFields.region)]: {
                    name: regionName,
                    text: regionName ?? regionCode,
                    data: {
                        code: regionCode,
                    },
                },
            };
        }

        return {};
    }

    private static buildHouseFiasValue(
        fiasField: FiasFields,
        extendedFields: ParsedExtendedFiasField[],
        fiasAddress: FiasAddress
    ): FiasFullValue["address"] {
        if (fiasField === FiasFields.garhouse) {
            const garhouseFieldName = String(FiasFields.garhouse);
            const houseNumExtendedField = extendedFields.find(it =>
                it.sourceValue.at(0)?.fiasApiPath?.endsWith("houseNum")
            );
            const houseNum: string = fiasAddress[houseNumExtendedField?.path ?? ""] ?? "";
            if (!houseNum) {
                return {};
            }

            const houseTextFields: Array<keyof GarHouse> = [
                "houseAbbreviation",
                "houseNum",
                "addAbbreviation1",
                "addNum1",
                "addAbbreviation2",
                "addNum2",
            ];
            const houseText = houseTextFields
                .map(houseTextField => {
                    const houseExtendedField = extendedFields.find(it =>
                        it.sourceValue.at(0)?.fiasApiPath?.endsWith(houseTextField)
                    );
                    const houseFieldValue: string = fiasAddress[houseExtendedField?.path ?? ""] ?? "";
                    return houseFieldValue;
                })
                .join(" ")
                .trim();

            const fiasValueAddressPart = {
                [garhouseFieldName]: {
                    name: houseNum,
                    text: houseText,
                    data: {
                        houseNum: houseNum,
                    },
                },
            };

            const houseExtraFields: Array<keyof GarHouse> = [
                "houseAbbreviation",
                "addNum1",
                "addAbbreviation1",
                "addNum2",
                "addAbbreviation2",
            ];
            for (const houseExtraFieldName of houseExtraFields) {
                const extendedField = extendedFields.find(it =>
                    it.sourceValue.at(0)?.fiasApiPath?.endsWith(houseExtraFieldName)
                );
                const value: string | undefined = fiasAddress[extendedField?.path ?? ""];
                if (value) {
                    // @ts-ignore
                    fiasValueAddressPart[garhouseFieldName].data[houseExtraFieldName] = value;
                }
            }

            return fiasValueAddressPart;
        }

        return {};
    }

    private static buildDivisionValue(
        extendedFields: ParsedExtendedFiasField[],
        fiasAddress: FiasAddress
    ): FiasFullValue["division"] {
        const extendedField = extendedFields.find(it => {
            const fiasApiPath = it.sourceValue.at(0)?.fiasApiPath ?? "";
            return fiasApiPath === "division";
        });
        const value: string | undefined = fiasAddress[extendedField?.path ?? ""];

        if (!value) {
            return undefined;
        }

        return convertDivisionType(value as divisionType);
    }
}
